import { StyledIconSmall, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { parseNumericValue, thousandSeparator } from '../../../../shared/utils/formatters.utils';

import { AccessorKeyColumnDef } from '@tanstack/table-core';
import { CellContext } from '@tanstack/react-table';
import { HiddenCompositionSharePAs } from '../../state/column-hiding.state';
import { Item } from '../../types/item.types';
import { SubItem } from '../../types/sub-item.types';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import warningEmpathizedTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle-colour';
import warningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';

const CellContainer = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   width: 100%;
   gap: ${tokens.space.md};
`;

const RightAlignedCell = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   width: 100%;
   gap: 0.5em;
`;

const IconSlot = styled.div`
   width: 24px;
   min-width: 24px;
   display: flex;
   justify-content: center;
   align-items: center;
`;

const WeightValue = styled.span`
   margin-right: calc(1.2em + 1em);
`;

const ErrorText = styled.span`
   color: ${theme.colors.state.error};
`;

const DisabledText = styled.span`
   color: ${theme.colors.text.disabled};
`;

const roundedNumericCell = (cell: CellContext<SubItem, unknown>) => {
   const value = parseNumericValue(cell.getValue());
   return thousandSeparator(Math.round(value));
};

type TableColumns = {
   main: AccessorKeyColumnDef<Item>[];
   expanded: AccessorKeyColumnDef<SubItem>[];
};

export const EMISSION_COLUMN_IDS = ['partsEmission', 'totalEmission'] as const;

export const filterEmissionColumnsByRole = <T extends { accessorKey?: string }>(
   columns: T[],
   isEmission: boolean
): T[] => {
   if (isEmission) {
      return columns;
   }

   return columns.filter((column) => !EMISSION_COLUMN_IDS.includes(column.accessorKey as (typeof EMISSION_COLUMN_IDS)[number]));
};

export const tableColumns: TableColumns = {
   main: [
      {
         header: 'Item number',
         accessorKey: 'itemNo',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'HFB',
         accessorKey: 'hfb',
      },
      {
         header: 'PA',
         accessorKey: 'pa',
      },
      {
         header: 'PRU',
         accessorKey: 'buCodePru',
      },
      {
         header: 'PDU',
         accessorKey: 'buCodePdu',
      },
      {
         header: 'Family name',
         accessorKey: 'familyName',
      },
      {
         header: 'Item name',
         accessorKey: 'itemName',
      },
      {
         header: 'Item state',
         accessorKey: 'itemState',
      },
      {
         header: 'Work progress',
         accessorKey: 'progress',
      },
      {
         header: 'PIA import status',
         accessorKey: 'piaImportStatus',
      },
      {
         header: 'Sale start date global',
         accessorKey: 'saleStartDateGlobal',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Sale end date global',
         accessorKey: 'saleEndDateGlobal',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'BoM valid from',
         accessorKey: 'bomValidFrom',
         cell: (cell: CellContext<Item, unknown>) => {
            const value = cell.getValue();
            if (value) {
               return (
                  <CellContainer>
                     <StyledTooltip title="Bill of material as designed">
                        <span>{value as string}</span>
                     </StyledTooltip>
                  </CellContainer>
               );
            }
            return <CellContainer />;
         },
         meta: {
            width: '10rem',
         },
      },
      {
         cell: (cell: CellContext<Item, unknown>) => {
            const isDwpAct = !(!cell.row.original.dwpActCpInfo || cell.row.original.dwpActCpInfo.suppliers.length === 0);
            return (
               <CellContainer>
                  <StyledTooltip title={isDwpAct ? 'DWP actual' : 'PIA (DWP required)'}>
                     <WeightValue>{thousandSeparator(cell.row.original.cpNetWeight)}</WeightValue>
                  </StyledTooltip>
               </CellContainer>
            );
         },
         header: 'Net weight\u00A0(g)',
         accessorKey: 'cpNetWeight',
         meta: {
            rightAligned: true,
            width: '8rem',
         },
      },
      {
         cell: (cell: CellContext<Item, unknown>) => {
            const item = cell.row.original;
            const resolvedWarningIcon =
               !item.isDwpDeviationWithinTolerance && item.isDwpDeviationWarningHidable
                  ? warningTriangleIcon
                  : warningEmpathizedTriangleIcon;

            const getTooltip = () => {
               if (!item.isDwpDeviationWarningHidable) {
                  return 'Mapped parts are not within tolerance (+/- 20%) compared to Net weight (g)';
               } else if (item.dwpWarningEnabled) {
                  return 'Mapped parts are not within tolerance (+/- 20%) compared to Net weight (g). This deviation could be due to components and may be ignored';
               } else {
                  return 'Mapped parts are not within tolerance (+/- 20%) compared to Net weight (g) but has been ignored due to components causing the deviation';
               }
            };

            const numberWithIcon = (
               <RightAlignedCell>
                  <span>{thousandSeparator(parseNumericValue(cell.getValue()))}</span>
                  <IconSlot>
                     {!item.isDwpDeviationWithinTolerance && (
                        <StyledIconSmall
                           paths={resolvedWarningIcon}
                           style={
                              !item.dwpWarningEnabled && item.isDwpDeviationWarningHidable
                                 ? { opacity: '0.25' }
                                 : undefined
                           }
                        />
                     )}
                  </IconSlot>
               </RightAlignedCell>
            );

            return item.isDwpDeviationWithinTolerance ? (
               numberWithIcon
            ) : (
               <StyledTooltip title={getTooltip()}>{numberWithIcon}</StyledTooltip>
            );
         },
         header: 'Weight of mapped parts\u00A0(g)',
         accessorKey: 'partsWeight',
         enableSorting: false,
         meta: {
            rightAligned: true,
            width: '10rem',
         },
      },
      {
         header: 'Recycled content\u00A0(%)',
         accessorKey: 'recycledContent',
         cell: (cell: CellContext<Item, unknown>) => (
            <DisabledText>{thousandSeparator(parseNumericValue(cell.getValue()))}</DisabledText>
         ),
         meta: {
            rightAligned: true,
            width: '8rem',
         },
      },
      {
         header: 'CO₂-e of mapped parts\u00A0(g)',
         accessorKey: 'partsEmission',
         cell: (cell: CellContext<Item, unknown>) => (
            <DisabledText>{thousandSeparator(parseNumericValue(cell.getValue()))}</DisabledText>
         ),
         meta: {
            rightAligned: true,
            width: '14rem',
         },
      },
      {
         header: '',
         accessorKey: 'action',
         enableSorting: false,
         cell: () => <></>,
         meta: {
            width: '10em',
         },
      },
   ],
   expanded: [
      {
         header: 'Part detail',
         accessorKey: 'partDetail',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Part',
         accessorKey: 'part',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Material grade',
         accessorKey: 'materialGrade',
         meta: {
            width: '30rem',
         },
      },
      {
         cell: (cell: CellContext<SubItem, unknown>) => {
            const value = cell.getValue() === '0' ? '' : (cell.getValue() as string);
            return cell.row.original.materialGradeIdWarning ? <ErrorText>{value}</ErrorText> : <span>{value}</span>;
         },
         header: 'MAM - Material grade ID',
         accessorKey: 'materialGradeId',
         meta: {
            width: '10rem',
         },
         enableSorting: true,
      },
      {
         header: 'Composition share\u00A0(%)',
         accessorKey: 'compositionShare',
         cell: (cell: CellContext<SubItem, unknown>) => {
            const value = cell.getValue() as string;
            const parentPA = cell.table.options.meta?.parentPA;
            const isDisabled = parentPA && HiddenCompositionSharePAs.includes(parentPA);

            const content = isDisabled ? <DisabledText>0</DisabledText> : <span>{value || ''}</span>;

            return isDisabled ? <StyledTooltip title="Not applicable for this PA">{content}</StyledTooltip> : content;
         },
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'MatSet name',
         accessorKey: 'matSetName',
         meta: {
            width: '20rem',
         },
      },
      {
         header: '3d model no.',
         accessorKey: 'threeDModelNo',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Total weight\u00A0(g)',
         cell: (cell: CellContext<SubItem, unknown>) => (
            <DisabledText>{thousandSeparator(parseNumericValue(cell.getValue()))}</DisabledText>
         ),
         accessorKey: 'totalWeight',
         meta: {
            width: '10rem',
            rightAligned: true,
         },
      },
      {
         header: 'CO₂-e\u00A0(g)',
         accessorKey: 'totalEmission',
         cell: (cell: CellContext<SubItem, unknown>) => (
            <DisabledText>{thousandSeparator(parseNumericValue(cell.getValue()))}</DisabledText>
         ),
         meta: {
            width: '10rem',
            rightAligned: true,
         },
      },
      {
         header: 'Quantity',
         accessorKey: 'quantity',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Mass\u00A0(g)',
         accessorKey: 'mass',
         cell: roundedNumericCell,
         meta: {
            rightAligned: true,
         },
      },
      {
         header: 'Volume\u00A0(mm³)',
         accessorKey: 'volume',
         cell: roundedNumericCell,
         meta: {
            rightAligned: true,
         },
      },
      {
         header: 'Density\u00A0(kg/m³)',
         accessorKey: 'density',
         cell: roundedNumericCell,
         meta: {
            rightAligned: true,
         },
      },
      {
         header: 'DWP weight\u00A0(g)',
         accessorKey: 'dwpTotalWeight',
         meta: {
            rightAligned: true,
         },
      },
      {
         header: 'Recycled content\u00A0(%)',
         accessorKey: 'recycledContent',
      },
      {
         header: 'Renewable content\u00A0(%)',
         accessorKey: 'renewableContent',
      },
      {
         header: 'Updated by',
         accessorKey: 'updatedBy',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Updated at',
         accessorKey: 'updatedAt',
         meta: {
            width: '10rem',
         },
      },
      {
         header: 'Comment',
         accessorKey: 'comment',
         meta: {
            width: '30rem',
         },
      },
   ],
};